import { Dispatch, SetStateAction } from 'react';
import {
  fileTypeIcon,
  FileType,
  UploaderProps,
} from 'components/Dropzone/utils';
import {
  createDropzoneFile,
  EnhancedFile,
  getFilePath,
  getFolderName,
  getIsInFolder,
} from 'uploadFileUtils';
import { getPercentageWithTwoDecimals } from 'utils';
import { ErrorCode, FileError, FileRejection } from 'react-dropzone';
import i18n from 'i18next';
import { MAX_FILES, MAX_SIZE_COPY } from '../data/DataTypes';
import { ERROR_TYPES, ERRORS_USER_FRIENDLY } from 'request-responses';
import { AxiosError } from 'axios';
import dataRoomApi from 'router/subrouters/DataRoom/dataRoomApi';
import { FILE_UPLOAD_ERROR_CODE } from 'router/subrouters/DataRoom/subrouters/Reports/pages/Reports/types';
import { MAX_PERCENTAGE } from 'data/global';
import { getAxiosErrorMessageCode } from 'axiosUtils';

const getError = (e: AxiosError) => {
  if (!e) return '';
  const errorCode = getAxiosErrorMessageCode(e);
  return i18n.t(
    ERRORS_USER_FRIENDLY[errorCode] ||
      ERRORS_USER_FRIENDLY[ERROR_TYPES.COULDNT_PARSE],
  );
};

const updateFailedFileConfig = (
  file: EnhancedFile,
  setUploaderProps: Dispatch<SetStateAction<Record<string, UploaderProps>>>,
  e: AxiosError,
) => {
  const path = getFilePath(file);

  const error = getError(e);

  setUploaderProps((old) => {
    if (!old[path]) return old;

    const prev = { ...old[path] };

    return {
      ...old,
      [path]: {
        ...prev,
        percentage: 0,
        isFailed: true,
        error,
      },
    };
  });
};

const updateFileConfig = (
  file: EnhancedFile,
  setUploaderProps: Dispatch<SetStateAction<Record<string, UploaderProps>>>,
) => ({
  onUploadProgress: (progressEvent: ProgressEvent) => {
    const { total, loaded } = progressEvent;
    const percentage = getPercentageWithTwoDecimals(loaded, total);

    const path = getFilePath(file);

    setUploaderProps((old) => {
      if (!old[path]) return old;

      const prev = { ...old[path] };

      return {
        ...old,
        [path]: {
          ...prev,
          percentage,
        },
      };
    });
  },
});

const createInitialFilesConfig = (
  files: EnhancedFile[],
  parentId: string,
  setUploaderProps: Dispatch<SetStateAction<Record<string, UploaderProps>>>,
) => {
  const filesConfig = files.reduce((acc, file) => {
    const path = getFilePath(file);
    const isInFolder = getIsInFolder(path);
    const folderName = isInFolder ? getFolderName(path) : '';

    acc[path] = {
      ...createDropzoneFile(file),
      icon: fileTypeIcon[file.type.split('/')[0] as FileType],
      folderName,
      parentId,
      percentage: 0,
    };

    return acc;
  }, {} as Record<string, UploaderProps>);

  setUploaderProps((old) => ({
    ...old,
    ...filesConfig,
  }));
};

const removeFailedOrUploaded = (
  setUploaderProps: Dispatch<SetStateAction<Record<string, UploaderProps>>>,
) => {
  setUploaderProps((old) =>
    Object.fromEntries(
      Object.entries(old).filter(
        ([, { percentage, isFailed }]) =>
          percentage !== MAX_PERCENTAGE && !isFailed,
      ),
    ),
  );
};

const removeFilesConfig = (
  paths: string[],
  setUploaderProps: Dispatch<SetStateAction<Record<string, UploaderProps>>>,
) =>
  setUploaderProps((old) => {
    const result = { ...old };

    paths.forEach((path) => delete result[path]);

    return result;
  });

const getDropzoneNativeError = (
  error: FileError,
  dataRoomId: string,
  documentName: string,
) => {
  if (error.code === ErrorCode.FileTooLarge) {
    if (documentName)
      dataRoomApi.reports.trackFileUploadError(dataRoomId, {
        documentName,
        errorCode: FILE_UPLOAD_ERROR_CODE.FILE_TOO_LARGE,
      });

    return i18n.t('fileLargerThan', { fileLimit: MAX_SIZE_COPY });
  }
  if (error.code === ErrorCode.FileTooSmall) return i18n.t('fileTooSmall');

  if (error.code === ErrorCode.FileInvalidType) {
    if (documentName)
      dataRoomApi.reports.trackFileUploadError(dataRoomId, {
        documentName,
        errorCode: FILE_UPLOAD_ERROR_CODE.FILE_INVALID_TYPE,
      });

    return i18n.t('invalidMimeTypeError');
  }

  return i18n.t('somethingWentWrong');
};

const createInitialFilesFailedConfig = (
  dataRoomId: string,
  fileRejections: FileRejection[],
  setUploaderProps: Dispatch<SetStateAction<Record<string, UploaderProps>>>,
) => {
  const filesConfig = fileRejections.reduce((acc, { file, errors }) => {
    const path = getFilePath(file);

    acc[path] = {
      ...createDropzoneFile(file),
      icon: fileTypeIcon[file.type.split('/')[0] as FileType],
      percentage: 0,
      isFailed: true,
      error: getDropzoneNativeError(errors[0], dataRoomId, file?.name),
    };
    return acc;
  }, {} as Record<string, UploaderProps>);

  setUploaderProps((old) => ({
    ...old,
    ...filesConfig,
  }));
};

const handleFileRejections = (
  dataRoomId: string,
  fileRejections: FileRejection[],
  setUploaderProps: Dispatch<SetStateAction<Record<string, UploaderProps>>>,
  popError: (content?: React.ReactNode) => void,
) => {
  if (!fileRejections?.length) return;

  const isTooManyFiles = fileRejections.some((fileRejection) =>
    fileRejection.errors.some((error) => error.code === ErrorCode.TooManyFiles),
  );

  if (isTooManyFiles) {
    popError(
      i18n.t('tooManyFilesError', {
        limit: MAX_FILES,
        numberOfFilesUploaded: fileRejections.length,
      }),
    );
    return;
  }

  createInitialFilesFailedConfig(dataRoomId, fileRejections, setUploaderProps);
};

const addNewVersionFileConfig = (
  file: EnhancedFile,
  replacingFileName: string,
  parentId: string,
  setUploaderProps: Dispatch<SetStateAction<Record<string, UploaderProps>>>,
) => {
  const path = getFilePath(file);

  setUploaderProps((old) => ({
    ...old,
    [path]: {
      ...createDropzoneFile(file),
      icon: fileTypeIcon[file.type.split('/')[0] as FileType],
      percentage: 0,
      parentId,
      replacingFileName,
    },
  }));
};

export {
  createInitialFilesConfig,
  createInitialFilesFailedConfig,
  updateFileConfig,
  updateFailedFileConfig,
  removeFilesConfig,
  handleFileRejections,
  addNewVersionFileConfig,
  removeFailedOrUploaded,
};
